import keycloak from '@/auth/keycloak';
import axios, { AxiosInstance } from 'axios';
import { useOrganization } from '@/contexts/organization-context';
import { useMemo } from 'react';

export const API_URL = import.meta.env.VITE_SERVICE_URL;
export const AUTH_URL = import.meta.env.VITE_KEYCLOAK_URL;

export const http = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add a request interceptor to add the bearer token
http.interceptors.request.use(async (config) => {
  // Skip authentication if explicitly requested
  if (config.headers['skip-auth']) {
    return config;
  }
  
  // Ensure token is fresh (at least 30 seconds left)
  if (keycloak.tokenParsed && keycloak.isTokenExpired(30)) {
    try {
      const refreshed = await keycloak.updateToken(60);
      if (refreshed) {
        console.log('Token refreshed successfully before API request');
      } else {
        console.log('Token still valid, no refresh needed');
      }
    } catch (error) {
      console.error('Error refreshing token before API request:', error);
      // Force login if token refresh fails
      keycloak.login();
      return Promise.reject('Authentication error');
    }
  }
  
  // Add the token to the request
  config.headers.Authorization = `Bearer ${keycloak.token}`;
  
  // Skip org/institution headers if explicitly requested
  if (!config.headers['skip-org-headers']) {
    // Add the organization ID to the headers if available
    const currentOrgId = localStorage.getItem('currentOrganizationId');
    if (currentOrgId) {
      config.headers['X-Organization-ID'] = currentOrgId;
    }
    
    // Add the institution ID to headers if available
    const savedInstitutionJson = localStorage.getItem('currentInstitution');
    if (savedInstitutionJson) {
      try {
        const savedInstitution = JSON.parse(savedInstitutionJson);
        if (savedInstitution && savedInstitution.id) {
          // Add institutionId as a header to all requests
          config.headers['x-institution-id'] = savedInstitution.id;
        }
      } catch (e) {
        console.error('Failed to parse saved institution:', e);
      }
    }
  } else {
    // Remove the skip flag from headers before sending the request
    delete config.headers['skip-org-headers'];
  }
  
  return config;
}, (error) => {
  return Promise.reject(error);
});

// Add a response interceptor to handle authentication errors
http.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    
    // Handle authentication errors (401)
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      try {
        // Try to refresh the token
        const refreshed = await keycloak.updateToken(30);
        if (refreshed) {
          console.log('Token refreshed after 401 error');
          // Update the authorization header
          originalRequest.headers.Authorization = `Bearer ${keycloak.token}`;
          // Retry the request
          return http(originalRequest);
        }
      } catch (refreshError) {
        console.error('Failed to refresh token after 401 error:', refreshError);
        // Redirect to login
        keycloak.login();
        return Promise.reject(refreshError);
      }
    }
    
    // Handle forbidden errors (403)
    if (error.response?.status === 403 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      try {
        // Token might be valid but lacking permissions, try refreshing anyway
        const refreshed = await keycloak.updateToken(5);
        if (refreshed) {
          console.log('Token refreshed after 403 error');
          originalRequest.headers.Authorization = `Bearer ${keycloak.token}`;
          return http(originalRequest);
        }
      } catch (refreshError) {
        console.error('Failed to refresh token after 403 error:', refreshError);
      }
    }
    
    return Promise.reject(error);
  },
);

// Add debug interceptors to log requests and responses
if (import.meta.env.DEV) {
  // Debug interceptor for http
  http.interceptors.request.use(
    (config) => {
      console.log('🚀 Request:', {
        method: config.method?.toUpperCase(),
        url: config.url,
        params: config.params,
        headers: {
          'Authorization': config.headers.Authorization ? 'Bearer ***' : 'MISSING',
          'X-Organization-ID': config.headers['X-Organization-ID'] || 'SKIPPED',
          'x-institution-id': config.headers['x-institution-id'] || 'SKIPPED',
          'Content-Type': config.headers['Content-Type'],
        },
      });
      return config;
    },
    (error) => {
      console.error('❌ Request Error:', error);
      return Promise.reject(error);
    }
  );

  http.interceptors.response.use(
    (response) => {
      console.log('✅ Response:', {
        status: response.status,
        statusText: response.statusText,
        url: response.config.url,
        isArray: Array.isArray(response.data),
        dataLength: Array.isArray(response.data) ? response.data.length : 'N/A',
      });
      return response;
    },
    (error) => {
      console.error('❌ Response Error:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        url: error.config?.url,
        message: error.response?.data?.message || error.message,
      });
      return Promise.reject(error);
    }
  );
}

/**
 * Custom hook that provides an API client with the current organization context.
 * Use this hook in components that need to make API calls in the context of the 
 * currently selected organization.
 * 
 * @returns An axios instance configured with organization headers
 */
export const useApiWithOrg = (): AxiosInstance => {
  const { currentOrganization, currentInstitution } = useOrganization();
  
  return useMemo(() => {
    // Create a new axios instance that extends the base configuration
    const orgApi = axios.create({
      baseURL: API_URL,
      headers: {
        'Content-Type': 'application/json',
      }
    });
    
    // Add the same response interceptor logic as the main http instance
    orgApi.interceptors.response.use(
      (response) => {
        return response;
      },
      async (error) => {
        const originalRequest = error.config;
        
        // Handle authentication errors (401)
        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;
          
          try {
            // Try to refresh the token
            const refreshed = await keycloak.updateToken(30);
            if (refreshed) {
              console.log('Token refreshed after 401 error');
              // Update the authorization header
              originalRequest.headers.Authorization = `Bearer ${keycloak.token}`;
              // Retry the request
              return orgApi(originalRequest);
            }
          } catch (refreshError) {
            console.error('Failed to refresh token after 401 error:', refreshError);
            // Redirect to login
            keycloak.login();
            return Promise.reject(refreshError);
          }
        }
        
        // Handle forbidden errors (403)
        if (error.response?.status === 403 && !originalRequest._retry) {
          originalRequest._retry = true;
          
          try {
            // Token might be valid but lacking permissions, try refreshing anyway
            const refreshed = await keycloak.updateToken(5);
            if (refreshed) {
              console.log('Token refreshed after 403 error');
              originalRequest.headers.Authorization = `Bearer ${keycloak.token}`;
              return orgApi(originalRequest);
            }
          } catch (refreshError) {
            console.error('Failed to refresh token after 403 error:', refreshError);
          }
        }
        
        return Promise.reject(error);
      },
    );
    
    // Add a request interceptor that includes organization context
    orgApi.interceptors.request.use(async (config) => {
      // Skip authentication if explicitly requested
      if (config.headers['skip-auth']) {
        return config;
      }
      
      // Ensure token is fresh (at least 30 seconds left)
      if (keycloak.tokenParsed && keycloak.isTokenExpired(30)) {
        try {
          const refreshed = await keycloak.updateToken(60);
          if (refreshed) {
            console.log('Token refreshed successfully before API request');
          } else {
            console.log('Token still valid, no refresh needed');
          }
        } catch (error) {
          console.error('Error refreshing token before API request:', error);
          // Force login if token refresh fails
          keycloak.login();
          return Promise.reject('Authentication error');
        }
      }
      
      // Add the token to the request
      config.headers.Authorization = `Bearer ${keycloak.token}`;
      
      // Add the organization ID from the context
      if (currentOrganization?.id) {
        config.headers['X-Organization-ID'] = currentOrganization.id;
      }
      
      // Add the institution ID to headers if available
      if (currentInstitution?.id) {
        // Add institutionId as a header to all requests
        config.headers['x-institution-id'] = currentInstitution.id;
      }
      
      // Debug log in development
      if (import.meta.env.DEV) {
        console.log('🚀 orgApi Request:', {
          method: config.method?.toUpperCase(),
          url: config.url,
          params: config.params,
          headers: {
            'Authorization': config.headers.Authorization ? 'Bearer ***' : 'MISSING',
            'X-Organization-ID': config.headers['X-Organization-ID'] || 'MISSING',
            'x-institution-id': config.headers['x-institution-id'] || 'MISSING',
            'Content-Type': config.headers['Content-Type'],
          },
          context: {
            org: currentOrganization?.id || 'null',
            institution: currentInstitution?.id || 'null'
          }
        });
      }
      
      return config;
    }, (error) => {
      return Promise.reject(error);
    });
    
    return orgApi;
  }, [currentOrganization, currentInstitution]);
};
